import React, { useState, useRef, useEffect } from 'react';
import { Query, useQuery } from 'react-apollo';
import { Switch, Route } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Divider, Tooltip, useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Helmet from 'react-helmet';
import MaterialTable from 'material-table';
import { Box, Card, CardContent } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { GET_ENGAGEMENT_DATA } from 'graphql/engagement';
import { GET_SETTINGS } from 'graphql/settings';
import StudentDetails from './components/StudentDetails';
import { useSelector } from 'react-redux';
import { toUserTimezone } from 'utils';
import Settings from './components/Settings';
import IOSSwitch from 'components/form/IOSSwitch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import DatePickerLabel from 'components/common/DatePickerLabel';
import Button from 'components/common/Button';
import FilterListIcon from '@material-ui/icons/FilterList';
import Modal from 'components/ui/modal';
import Dropdown from 'components/form/Dropdown';


const useStyles = makeStyles((theme) => ({
  cardcontent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1em',
  },
  reset: {
    borderRadius: 10,
  },
  cardTitle: {
    margin: '1em',
  },
  mobileCardcontent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '1em',
  },
  mobilecardTitle: {
    textAlign: 'center',
  },
  datePicker: {
    'fontSize': 14,
    '& .MuiInput-input:not(.Mui-disabled)': {
      fontSize: 14,
      cursor: 'pointer',
      color: theme.palette.text.secondary,
    }
  },
  green: {
    display: 'inline-block',
    border: '1px solid #acffa3',
    backgroundColor: '#acffa3',
    padding: '2px 3px',
    borderRadius: '5px'

  },
  yellow: {
    display: 'inline-block',
    border: '1px solid #fff7b3',
    backgroundColor: '#fff7b3',
    margin: '5px',
    padding: '2px 3px',
    borderRadius: '5px'

  },
  red: {
    display: 'inline-block',
    border: '1px solid #ff8f8f',
    margin: '5px',
    backgroundColor: '#ff8f8f',
    padding: '2px 3px',
    borderRadius: '5px',

  },
  awaiting: {
    display: 'inline-block',
    margin: '5px',
    border: '1px solid black',
    backgroundColor: 'black',
    color: 'white',
    padding: '2px 3px',
    borderRadius: '5px',
  },
}));

function pluck_gradYear(array, key) {
  return array?.rows.map((o) => o[key]);
}
function pluck_score(array, key) {
  return array?.rows.map((o) => o.engagement[key]).sort();
}

const Engagement = ({ history }) => {
  const classes = useStyles();

  const { timezone, type } = useSelector((s) => s.user.user);
  const [showSettings, setShowSettings] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const [score, setScore] = useState();
  const [class_Year, setClass_Year] = useState();
  const [last_Login, setLast_Login] = useState();
  const [next_Scheduled_Meeting, setNext_Scheduled_Meeting] = useState();
  const [lastText, setLastText] = useState();
  const [lastChat, setLastChat] = useState();
  const [isMobile, setIsMobile] = useState(false);
  const { data } = useQuery(GET_ENGAGEMENT_DATA);

  const { data2 } = useQuery(GET_SETTINGS, {
    variables: { key: 'ENGAGEMENT_CONFIG' },
    fetchPolicy: 'cache-and-network',
    onCompleted: (val) => {
      if (val) {
        const { value } = val.settings;
        const mins = {
          high: parseInt(value.min_high_score),
          mid: parseInt(value.min_mid_score),
          low: parseInt(value.min_low_score),
        };
        setColors(mins);
      }
    },
  });

  const [colors, setColors] = useState({
    high: null,
    mid: null,
    low: null,
  });

  let mins = {
    redCount: 0,
    yellowCount: 0,
    greenCount: 0,
    awaitingResponse: 0,
  };
  let greenC = 0;
  let yellowC = 0;
  let redC = 0;
  let awaitC = 0;

  useEffect(() => {
    document.getElementById(
      'greenCounter',
    ).innerHTML = `Green: ${mins.greenCount}`;
    document.getElementById(
      'yellowCounter',
    ).innerHTML = `Yellow: ${mins.yellowCount}`;
    document.getElementById('redCounter').innerHTML = `Red: ${mins.redCount}`;
    document.getElementById(
      'awaitCounter',
    ).innerHTML = `Awaiting Response: ${mins.awaitingResponse}`;
  }, [mins]);

  const tableRef = useRef();

  let scores = pluck_score(data, 'score');
  let gradYear = pluck_gradYear(data, 'graduation_year');

  let unique_score = [...new Set(scores)];
  let unique_gradYear = [...new Set(gradYear)];

  let filtered_score = unique_score.filter(
    (el) => el !== null && el !== undefined,
  );
  let filtered_gradYear = unique_gradYear.filter((el) => el !== null);

  const YEAR_OPTIONS = filtered_gradYear.map((i) => ({ label: i, value: i }));

  let max_score_data = Math.ceil(Math.max(...filtered_score) / 3);
  const SCORE_OPTIONS = Array.from({ length: max_score_data }, (_, i) => ({
    label: `${i * 3} - ${i * 3 + 3}`,
    value: `${i * 3 + 3}`,
  }));

  const resetAllFilter = () => {
    setScore();
    setClass_Year();
    setLast_Login();
    setNext_Scheduled_Meeting();
    setLastText();
    setLastChat();
  };

  const closeHandle = () => {
    setIsMobile(false);
    resetAllFilter();
  };

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(1300));

  const columns = [
    {
      title: 'Score',
      field: 'score',
      type: 'numeric',
      customFilterAndSearch: (term, rowData) => {
        if (
          rowData.score <= term &&
          rowData.score >= term - 3 &&
          rowData.score !== null
        ) {
          if (
            colors.high !== null ||
            colors.mid !== null ||
            colors.low !== null
          ) {
            if (rowData.score >= colors.high) {
              greenC = greenC + 1;
            } else if (rowData.score >= colors.mid) {
              yellowC = yellowC + 1;
            } else if (rowData.score >= colors.low) {
              redC = redC + 1;
            }

            if (
              rowData.properties.chatAwatingResponse === true ||
              rowData.properties.textAwatingResponse === true
            ) {
              awaitC = awaitC + 1;
            }
          }
        }
        mins = {
          redCount: redC,
          greenCount: greenC,
          yellowCount: yellowC,
          awaitingResponse: awaitC,
        };
        return (
          rowData.score <= term &&
          rowData.score >= term - 3 &&
          rowData.score !== null
        );
      },
      defaultFilter: score,
    },
    {
      title: 'Student Name',
      field: 'user_first_name',
      render: (row) => row.user_first_name + ' ' + row.user_last_name,
      customFilterAndSearch: (term, rowData) => {
        if (
          rowData.user_first_name.toLowerCase().indexOf(term.toLowerCase()) !==
          -1 ||
          rowData.user_last_name.toLowerCase().indexOf(term.toLowerCase()) !==
          -1
        ) {
          if (
            colors.high !== null ||
            colors.mid !== null ||
            colors.low !== null
          ) {
            if (rowData.score >= colors.high) {
              greenC = greenC + 1;
            } else if (rowData.score >= colors.mid) {
              yellowC = yellowC + 1;
            } else if (rowData.score >= colors.low) {
              redC = redC + 1;
            }

            if (
              rowData.properties.chatAwatingResponse === true ||
              rowData.properties.textAwatingResponse === true
            ) {
              awaitC = awaitC + 1;
            }
          }
        }
        mins = {
          redCount: redC,
          greenCount: greenC,
          yellowCount: yellowC,
          awaitingResponse: awaitC,
        };

        return (
          rowData.user_first_name.toLowerCase().indexOf(term.toLowerCase()) !==
          -1 ||
          rowData.user_last_name.toLowerCase().indexOf(term.toLowerCase()) !==
          -1
        );
      },
    },
    {
      title: 'Class year',
      field: 'graduation_year',
      defaultFilter: class_Year,
      customFilterAndSearch: (term, rowData) => {
        if (rowData.graduation_year?.toString() === term) {
          if (
            colors.high !== null ||
            colors.mid !== null ||
            colors.low !== null
          ) {
            if (rowData.score >= colors.high) {
              greenC = greenC + 1;
            } else if (rowData.score >= colors.mid) {
              yellowC = yellowC + 1;
            } else if (rowData.score >= colors.low) {
              redC = redC + 1;
            }

            if (
              rowData.properties.chatAwatingResponse === true ||
              rowData.properties.textAwatingResponse === true
            ) {
              awaitC = awaitC + 1;
            }
          }
        }
        mins = {
          redCount: redC,
          greenCount: greenC,
          yellowCount: yellowC,
          awaitingResponse: awaitC,
        };

        return rowData.graduation_year?.toString() === term;
      },
    },
    {
      title: 'Last Login',
      field: 'last_login_at',
      type: 'date',
      render: (row) =>
        row.last_login_at &&
        toUserTimezone(row.last_login_at, timezone).format('L'),
      defaultFilter: last_Login,
      customFilterAndSearch: (term, rowData) => {
        if (typeof term !== 'string') {
          if (
            rowData.last_login_at !== null &&
            rowData.last_login_at.substring(0, 10) ===
            term?.toISOString().substr(0, 10)
          ) {
            if (
              colors.high !== null ||
              colors.mid !== null ||
              colors.low !== null
            ) {
              if (rowData.score >= colors.high) {
                greenC = greenC + 1;
              } else if (rowData.score >= colors.mid) {
                yellowC = yellowC + 1;
              } else if (rowData.score >= colors.low) {
                redC = redC + 1;
              }

              if (
                rowData.properties.chatAwatingResponse === true ||
                rowData.properties.textAwatingResponse === true
              ) {
                awaitC = awaitC + 1;
              }
            }
          }
          mins = {
            redCount: redC,
            greenCount: greenC,
            yellowCount: yellowC,
            awaitingResponse: awaitC,
          };
          return (
            rowData.last_login_at !== null &&
            rowData.last_login_at.substring(0, 10) ===
            term?.toISOString().substr(0, 10)
          );
        }
      },
    },
    {
      title: 'Last Completed Meeting',
      field: 'timestamps.last_completed_cc_meeting',
      render: (row) => {
        return (
          <>
            {row.timestamps.last_completed_cc_meeting && (
              <div>
                CC:&nbsp;
                {toUserTimezone(
                  row.timestamps.last_completed_cc_meeting,
                  timezone,
                ).format('L')}
              </div>
            )}
            {row.timestamps.last_completed_hc_meeting && (
              <div>
                HC:&nbsp;
                {toUserTimezone(
                  row.timestamps.last_completed_hc_meeting,
                  timezone,
                ).format('L')}
              </div>
            )}
          </>
        );
      },
      filterPlaceholder: 'Completed',
    },
    {
      title: 'Next Scheduled Meeting',
      field: 'timestamps.next_cc_meeting',
      render: (row) => {
        return (
          <>
            {row.timestamps.next_cc_meeting && (
              <div>
                CC:&nbsp;
                {toUserTimezone(
                  row.timestamps.next_cc_meeting,
                  timezone,
                ).format('L')}
              </div>
            )}
            {row.timestamps.next_hc_meeting && (
              <div>
                HC:&nbsp;
                {toUserTimezone(
                  row.timestamps.next_hc_meeting,
                  timezone,
                ).format('L')}
              </div>
            )}
          </>
        );
      },
      filterPlaceholder: 'Scheduled',
      defaultFilter: next_Scheduled_Meeting,
    },
    {
      title: 'Last Text',
      field: 'timestamps.last_student_sms',
      render: (row) =>
        row.timestamps.last_student_sms &&
        toUserTimezone(row.timestamps.last_student_sms, timezone).format('L'),
      defaultFilter: lastText,
      customFilterAndSearch: (term, rowData) => {
        if (rowData.timestamps.last_student_sms?.substring(0, 10) === term) {
          if (
            colors.high !== null ||
            colors.mid !== null ||
            colors.low !== null
          ) {
            if (rowData.score >= colors.high) {
              greenC = greenC + 1;
            } else if (rowData.score >= colors.mid) {
              yellowC = yellowC + 1;
            } else if (rowData.score >= colors.low) {
              redC = redC + 1;
            }

            if (
              rowData.properties.chatAwatingResponse === true ||
              rowData.properties.textAwatingResponse === true
            ) {
              awaitC = awaitC + 1;
            }
          }
        }
        mins = {
          redCount: redC,
          greenCount: greenC,
          yellowCount: yellowC,
          awaitingResponse: awaitC,
        };
        return rowData.timestamps.last_student_sms?.substring(0, 10) === term;
      },
    },
    {
      title: 'Last Chat',
      field: 'timestamps.last_student_chat',
      render: (row) =>
        row.timestamps.last_student_chat &&
        toUserTimezone(row.timestamps.last_student_chat, timezone).format('L'),
      defaultFilter: lastChat,
      customFilterAndSearch: (term, rowData) => {
        if (rowData.timestamps.last_student_chat?.substring(0, 10) === term) {
          if (
            colors.high !== null ||
            colors.mid !== null ||
            colors.low !== null
          ) {
            if (rowData.score >= colors.high) {
              greenC = greenC + 1;
            } else if (rowData.score >= colors.mid) {
              yellowC = yellowC + 1;
            } else if (rowData.score >= colors.low) {
              redC = redC + 1;
            }

            if (
              rowData.properties.chatAwatingResponse === true ||
              rowData.properties.textAwatingResponse === true
            ) {
              awaitC = awaitC + 1;
            }
          }
        }
        mins = {
          redCount: redC,
          greenCount: greenC,
          yellowCount: yellowC,
          awaitingResponse: awaitC,
        };
        return rowData.timestamps.last_student_chat?.substring(0, 10) === term;
      },
    },
    {
      title: 'Chat/Text awaiting response for 24h',
      field: 'properties',
      render: (row) => {
        if (
          row.properties.textAwatingResponse ||
          row.properties.chatAwatingResponse
        ) {
          return <WarningIcon />;
        }
      },
      filtering: false,
    },
    {
      title: 'Essay review pending for 48h',
      field: 'properties',
      render: (row) => {
        if (row.properties.essayPendingReview) {
          return <WarningIcon />;
        }
      },
    },
    {
      title: 'Last CC view of student',
      field: 'timestamps.last_cc_activity',
      type: 'date',
      render: (row) =>
        row.timestamps.last_cc_activity &&
        toUserTimezone(row.timestamps.last_cc_activity, timezone).format('L'),
    },
    { title: 'Notes', field: 'notes', filtering: false },
  ];

  return (
    <div>
      {matches ? (
        <Tooltip title="Filter" placement="top">
          <FilterListIcon
            style={{ marginBottom: 10 }}
            onClick={() => setIsMobile(!isMobile)}
          />
        </Tooltip>
      ) : (
        <>
          <Typography paragraph variant="h5">
            Filters
          </Typography>
          <Card>
            <Typography
              className={classes.cardTitle}
              paragraph
              variant="subtitle2"
            >
              Filter by
            </Typography>

            <CardContent className={classes.cardcontent}>
              <Box paddingRight={10}>
                <Typography
                  gutterBottom
                  variant="smallBold"
                  color="textSecondary"
                  component="p"
                >
                  Score
                </Typography>
                <Dropdown
                  style={{ width: 150 }}
                  options={SCORE_OPTIONS}
                  value={score}
                  onChange={(val) => {
                    let index = val;
                    setScore(index);
                  }}
                />
              </Box>

              <Box paddingRight={10}>
                <Typography
                  gutterBottom
                  variant="smallBold"
                  color="textSecondary"
                  component="p"
                >
                  Class Year
                </Typography>
                <Dropdown
                  style={{ width: 150 }}
                  options={YEAR_OPTIONS}
                  value={parseInt(class_Year)}
                  onChange={(val) => setClass_Year(val.toString())}
                />
              </Box>

              <Box>
                <Typography
                  gutterBottom
                  variant="smallBold"
                  color="textSecondary"
                  component="p"
                >
                  Last Login
                </Typography>
                <DatePickerLabel
                  value={last_Login || null}
                  onChange={(e) => setLast_Login(e._d)}
                />
              </Box>
              <Box>
                <Typography
                  gutterBottom
                  variant="smallBold"
                  color="textSecondary"
                  component="p"
                >
                  Last Text
                </Typography>
                <DatePickerLabel
                  value={lastText || null}
                  onChange={(e) =>
                    setLastText(e._d.toISOString().substring(0, 10))
                  }
                />
              </Box>
              <Box>
                <Typography
                  gutterBottom
                  variant="smallBold"
                  color="textSecondary"
                  component="p"
                >
                  Last Chat
                </Typography>
                <DatePickerLabel
                  value={lastChat || null}
                  onChange={(e) => {
                    setLastChat(e._d.toISOString().substr(0, 10));
                  }}
                />
              </Box>

              <Box margin={1}>
                <Button
                  className={classes.reset}
                  fullWidth
                  variant="outlined"
                  size="large"
                  color="primary"
                  onClick={resetAllFilter}
                >
                  Reset
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
      <br />
      {isMobile && (
        <Modal
          fullWidth
          open
          className={classes.modal}
          onClose={closeHandle}
          actions={[
            <Box>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                color="primary"
                onClick={resetAllFilter}
              >
                Reset
              </Button>
            </Box>,
            <Button
              fullWidth
              variant="contained"
              size="large"
              color="primary"
              onClick={() => setIsMobile(false)}
            >
              Show Result
            </Button>,
          ]}
        >
          <Typography
            className={classes.mobilecardTitle}
            paragraph
            variant="h5"
          >
            Filter by
          </Typography>
          <Divider />
          <Box paddingLeft={5} lineHeight={2.5}>
            <Box>
              <Typography
                gutterBottom
                variant="smallBold"
                color="textSecondary"
                component="p"
              >
                Score
              </Typography>
              <Dropdown
                style={{ width: 150 }}
                options={SCORE_OPTIONS}
                value={score}
                onChange={(val) => {
                  let index = val;
                  setScore(index);
                }}
              />
            </Box>
            <Box>
              <Typography
                gutterBottom
                variant="smallBold"
                color="textSecondary"
                component="p"
              >
                Class Year
              </Typography>
              <Dropdown
                style={{ width: 120 }}
                options={YEAR_OPTIONS}
                value={parseInt(class_Year)}
                onChange={(val) => setClass_Year(val.toString())}
              />
            </Box>
            <Box>
              <Typography
                gutterBottom
                variant="smallBold"
                color="textSecondary"
                component="p"
              >
                Last Login
              </Typography>
              <DatePickerLabel
                label=""
                value={last_Login || null}
                onChange={(e) => setLast_Login(e._d)}
              />
            </Box>
            <Box>
              <Typography
                gutterBottom
                variant="smallBold"
                color="textSecondary"
                component="p"
              >
                Last Text
              </Typography>

              <DatePickerLabel
                value={lastText || null}
                onChange={(e) =>
                  setLastText(e._d.toISOString().substring(0, 10))
                }
              />
            </Box>
            <Box>
              <Typography
                gutterBottom
                variant="smallBold"
                color="textSecondary"
                component="p"
              >
                Last Chat
              </Typography>
              <DatePickerLabel
                clearable
                value={lastChat || null}
                onChange={(e) =>
                  setLastChat(e._d.toISOString().substring(0, 10))
                }
              />
            </Box>
          </Box>
        </Modal>
      )}

      <Box>
        <Helmet title="Student Engagement" />
        <Query
          query={GET_ENGAGEMENT_DATA}
          variables={{
            expired: showAll,
          }}
        >
          {({ data, loading }) => {
            let greenC = 0;
            let yellowC = 0;
            let redC = 0;
            let awaitC = 0;
            const rows = data?.rows.map((row, index) => {
              if (
                colors.high !== null ||
                colors.mid !== null ||
                colors.low !== null
              ) {
                if (row.engagement.score >= colors.high) {
                  greenC = greenC + 1;
                } else if (row.engagement.score >= colors.mid) {
                  yellowC = yellowC + 1;
                } else if (row.engagement.score >= colors.low) {
                  redC = redC + 1;
                }

                if (
                  row.engagement.properties.chatAwatingResponse === true ||
                  row.engagement.properties.textAwatingResponse === true
                ) {
                  awaitC = awaitC + 1;
                }
              }
              if (index === data?.rows.length - 1) {
                mins = {
                  redCount: redC,
                  greenCount: greenC,
                  yellowCount: yellowC,
                  awaitingResponse: awaitC,
                };
              }

              return {
                ...row.engagement,
                ...row,
              };
            });

            return (
              <div>
                <span id="greenCounter" className={classes.green}>
                  Green : {mins.greenCount}
                </span>
                <span id="yellowCounter" className={classes.yellow}>
                  Yellow : {mins.yellowCount}
                </span>
                <span id="redCounter" className={classes.red}>
                  Red : {mins.redCount}
                </span>
                <span id="awaitCounter" className={classes.awaiting}>
                  Total Awaiting Response : {mins.awaitingResponse}
                </span>
                <MaterialTable
                  tableRef={tableRef}
                  icons={{ Filter: () => <div /> }}
                  isLoading={loading}
                  title={
                    <Box ml={1}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={showAll}
                            onChange={(e) => setShowAll(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Show Past students"
                      />
                    </Box>
                  }
                  columns={columns}
                  data={rows}
                  actions={
                    type === 'admin' && [
                      {
                        isFreeAction: true,
                        icon: 'settings',
                        onClick: () => setShowSettings(true),
                      },
                    ]
                  }
                  options={{
                    pageSize: 10,
                    sorting: true,
                    rowStyle: ({ score }) => {
                      let color;
                      if (score >= colors.high) color = '#acffa3';
                      else if (score >= colors.mid) color = '#fff7b3';
                      else if (score >= colors.low) color = '#ff8f8f';

                      return { backgroundColor: color };
                    },
                    search: true,
                    filtering: false,
                    filterCellStyle: {
                      position: 'relative',
                      top: '0px',
                      right: '0px',
                    },
                  }}
                  onRowClick={(e, row) =>
                    window.open(`/engagement/students/${row.user_id}`, '_blank')
                  }
                />
              </div>
            );
          }}
        </Query>
        <Box mt={1}>
          <Typography variant="caption">
            Note: this report is updated every 6 hours.
          </Typography>
        </Box>

        <Switch>
          <Route
            exact
            path="/engagement/students/:id"
            component={StudentDetails}
          />
        </Switch>
        {showSettings && (
          <Settings
            setColors={setColors}
            onClose={() => setShowSettings(false)}
          />
        )}
      </Box>
    </div>
  );
};

export default Engagement;
